<template>
  <section id="hero" class="hero-section">

    <!-- Background -->
    <img
      ref="bgImg"
      :src="require('@/assets/dumbbellBackground.webp')"
      class="hero-bg"
      alt=""
      fetchpriority="high"
      loading="eager"
    />
    <div class="hero-overlay"></div>

    <!-- Main content -->
    <div class="container hero-body">
      <div class="row align-items-center hero-row">

        <!-- ── Left: Text ── -->
        <div class="col-12 col-lg-6 hero-text">

          <div class="hero-pill animate-fade-up" style="animation-delay:.1s">
            <span class="hero-pill-dot"></span>
            24/7 GEÖFFNET
          </div>

          <h1 class="hero-headline animate-fade-up" style="animation-delay:.2s">
            Dein&nbsp;Gym.<br />
            <span class="hero-headline-green">Jederzeit.</span>
          </h1>

          <p class="hero-sub animate-fade-up" style="animation-delay:.32s">
            Trainiere flexibel, rund um die Uhr&nbsp;–&nbsp;ganz ohne Einschränkungen.
          </p>

          <div class="hero-actions animate-fade-up" style="animation-delay:.42s">
            <button class="hero-btn hero-btn-solid" @click="goToServices">
              Mitglied werden
            </button>
            <button class="hero-btn hero-btn-ghost" @click="goToAbout">
              Mehr erfahren
            </button>
          </div>

          <div class="hero-stats animate-fade-up" style="animation-delay:.54s">
            <div class="hero-stat">
              <span class="hero-stat-val">24/7</span>
              <span class="hero-stat-lbl">Geöffnet</span>
            </div>
            <div class="hero-stat-sep"></div>
            <div class="hero-stat">
              <span class="hero-stat-val">Top</span>
              <span class="hero-stat-lbl">Equipment</span>
            </div>
            <div class="hero-stat-sep"></div>
            <div class="hero-stat">
              <span class="hero-stat-val">100%</span>
              <span class="hero-stat-lbl">Flexibel</span>
            </div>
          </div>
        </div>

        <!-- ── Right: Image Collage ── -->
        <div class="col-lg-6 d-none d-lg-flex justify-content-center">
          <div class="hero-collage">

            <!-- Primary: mainarea -->
            <div class="hero-frame hero-frame-a animate-img-a">
              <img
                :src="require('@/assets/mainarea.webp')"
                alt="DailyFit Trainingsbereich"
                class="hero-frame-img"
                loading="eager"
              />
              <div class="hero-frame-sheen"></div>
              <img
                :src="require('@/assets/logo_ohne_Hintergrund.webp')"
                alt="DailyFit"
                class="hero-logo"
              />
            </div>

            <!-- Secondary: latpull -->
            <div class="hero-frame hero-frame-b animate-img-b">
              <img
                :src="require('@/assets/latpull.webp')"
                alt="DailyFit Equipment"
                class="hero-frame-img"
                loading="eager"
              />
            </div>

            <!-- Decorative corner bracket -->
            <div class="hero-bracket"></div>

          </div>
        </div>

      </div>
    </div>

  </section>
</template>

<script>
export default {
  name: 'HeroSection',
  mounted() {
    window.addEventListener('scroll', this.onParallax, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onParallax);
  },
  methods: {
    onParallax() {
      if (this.$refs.bgImg) {
        this.$refs.bgImg.style.transform = `translateY(${window.scrollY * 0.38}px)`;
      }
    },
    goToServices() {
      document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
    },
    goToAbout() {
      document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
    },
  },
};
</script>

<style scoped>
/* ─── Base ──────────────────────────────── */
.hero-section {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  top: -15%;
  left: 0;
  width: 100%;
  height: 130%;
  object-fit: cover;
  object-position: center;
  z-index: 0;
  will-change: transform;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    130deg,
    rgba(0, 0, 0, 0.72) 0%,
    rgba(0, 0, 0, 0.60) 55%,
    rgba(0, 0, 0, 0.38) 100%
  );
  z-index: 1;
}

.hero-body {
  position: relative;
  z-index: 2;
  padding-top: 96px;
  padding-bottom: 72px;
}

.hero-row {
  min-height: calc(100vh - 168px);
}

/* ─── Logo (zentriert auf Hauptbild) ───── */
.hero-logo {
  position: absolute;
  bottom: 16px;
  right: 16px;
  width: 45%;
  height: auto;
  opacity: 0.45;
  filter: drop-shadow(0 0 24px rgba(124, 255, 0, 0.4)) brightness(1.2);
  pointer-events: none;
  z-index: 2;
}

/* ─── Pill badge ────────────────────────── */
.hero-pill {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  background: rgba(124, 255, 0, 0.08);
  border: 1px solid rgba(124, 255, 0, 0.28);
  color: #7cff00;
  padding: 0.42rem 1.1rem;
  border-radius: 50px;
  font-family: 'Outfit', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 2.2px;
  text-transform: uppercase;
  margin-bottom: 1.75rem;
  width: fit-content;
}

.hero-pill-dot {
  width: 7px;
  height: 7px;
  background: #7cff00;
  border-radius: 50%;
  flex-shrink: 0;
  animation: dot-pulse 2.2s ease-in-out infinite;
}

@keyframes dot-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(124, 255, 0, 0.55); }
  50%       { box-shadow: 0 0 0 7px rgba(124, 255, 0, 0); }
}

/* ─── Headline ──────────────────────────── */
.hero-headline {
  font-family: 'Bebas Neue', 'Arial Black', sans-serif;
  font-size: clamp(4rem, 7.5vw, 7rem);
  line-height: 0.93;
  letter-spacing: 1px;
  color: #ffffff;
  margin-bottom: 1.6rem;
  font-weight: 400;
}

.hero-headline-green {
  color: #7cff00;
  display: block;
}

/* ─── Subtext ───────────────────────────── */
.hero-sub {
  font-family: 'Outfit', sans-serif;
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.58);
  margin-bottom: 2.4rem;
  line-height: 1.55;
  max-width: 370px;
}

/* ─── Buttons ───────────────────────────── */
.hero-actions {
  display: flex;
  gap: 0.9rem;
  flex-wrap: wrap;
  margin-bottom: 2.75rem;
}

.hero-btn {
  font-family: 'Outfit', sans-serif;
  font-weight: 600;
  font-size: 0.93rem;
  padding: 0.82rem 1.9rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease, border-color 0.22s ease;
  letter-spacing: 0.2px;
  display: inline-flex;
  align-items: center;
}

.hero-btn-solid {
  background: #7cff00;
  color: #000;
}
.hero-btn-solid:hover {
  background: #9bff2e;
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(124, 255, 0, 0.38);
}
.hero-btn-solid:active { transform: translateY(0); }

.hero-btn-ghost {
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  border: 1.5px solid rgba(255, 255, 255, 0.2);
}
.hero-btn-ghost:hover {
  border-color: rgba(255, 255, 255, 0.5);
  color: #fff;
  transform: translateY(-2px);
}
.hero-btn-ghost:active { transform: translateY(0); }

/* ─── Stats ─────────────────────────────── */
.hero-stats {
  display: flex;
  align-items: center;
  gap: 1.6rem;
  padding-top: 1.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.hero-stat {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.hero-stat-val {
  font-family: 'Outfit', sans-serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: #7cff00;
  line-height: 1;
}

.hero-stat-lbl {
  font-family: 'Outfit', sans-serif;
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.38);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.hero-stat-sep {
  width: 1px;
  height: 32px;
  background: rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

/* ─── Image Collage ─────────────────────── */
.hero-collage {
  position: relative;
  width: 440px;
  height: 490px;
}

.hero-frame {
  position: absolute;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.65);
}

.hero-frame-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}
.hero-frame:hover .hero-frame-img {
  transform: scale(1.05);
}

/* Primary frame: top-right, tall */
.hero-frame-a {
  top: 0;
  right: 0;
  width: 270px;
  height: 390px;
  border: 1.5px solid rgba(124, 255, 0, 0.18);
}

/* Subtle green sheen on primary */
.hero-frame-sheen {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    160deg,
    rgba(124, 255, 0, 0.07) 0%,
    transparent 45%
  );
  pointer-events: none;
}

/* Secondary frame: bottom-left, landscape, slight tilt */
.hero-frame-b {
  bottom: 0;
  left: 0;
  width: 240px;
  height: 172px;
  transform: rotate(-4deg);
  border: 1.5px solid rgba(255, 255, 255, 0.07);
}

/* Decorative bracket — top-left corner accent */
.hero-bracket {
  position: absolute;
  top: -12px;
  left: 60px;
  width: 36px;
  height: 36px;
  border-top: 2.5px solid #7cff00;
  border-left: 2.5px solid #7cff00;
  border-radius: 3px 0 0 0;
  opacity: 0.55;
}

/* ─── Entry Animations ───────────────────── */
.animate-fade-up {
  opacity: 0;
  animation: fade-up 0.65s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

@keyframes fade-up {
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
}

.animate-img-a {
  opacity: 0;
  animation: img-reveal-a 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.35s forwards;
}

@keyframes img-reveal-a {
  from { opacity: 0; transform: translateY(30px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

.animate-img-b {
  opacity: 0;
  animation: img-reveal-b 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.52s forwards;
}

@keyframes img-reveal-b {
  from { opacity: 0; transform: rotate(-4deg) scale(0.95); }
  to   { opacity: 1; transform: rotate(-4deg) scale(1); }
}

/* ─── Responsive ─────────────────────────── */
@media (max-width: 1199px) {
  .hero-collage {
    width: 380px;
    height: 430px;
  }
  .hero-frame-a {
    width: 240px;
    height: 345px;
  }
  .hero-frame-b {
    width: 210px;
    height: 155px;
  }
}

@media (max-width: 991px) {
  .hero-body {
    padding-top: 110px;
    padding-bottom: 60px;
  }
  .hero-bg {
    object-position: 20% center;
  }
  .hero-logo {
    width: 170px;
    margin-bottom: 1.75rem;
  }
  .hero-sub {
    max-width: 100%;
    font-size: 1rem;
  }
}

@media (max-width: 575px) {
  .hero-logo {
    width: 145px;
    margin-bottom: 1.5rem;
  }
  .hero-actions {
    flex-direction: column;
  }
  .hero-btn {
    width: 100%;
    justify-content: center;
  }
  .hero-stats {
    gap: 1.1rem;
    justify-content: center;
  }
  .hero-stat {
    align-items: center;
  }
  .hero-stat-val {
    font-size: 1.15rem;
  }
}
</style>
